/* global DetailsModal, CompareUtil */

customElements.whenDefined('details-modal').then(() => {
  class CompareModal extends DetailsModal {
    constructor() {
      super();
      this.compareContainer = this.querySelector('.compare-container');
      this.compareArea = this.querySelector('.js-compare-area');
      this.differences = this.querySelector('.js-compare-differences');
      this.differencesGrid = this.querySelector('.js-compare-differences-grid');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.errorArea = this.querySelector('.js-compare-error');
      this.closeButtons = this.querySelectorAll('.js-compare-close');
      this.copy = CompareModal.readCopy();
      this.open = this.open.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleClick);
      this.removeEventListener('on:product-compare-modal:open', this.open);
    }

    bindEvents() {
      this.addEventListener('on:product-compare-modal:open', this.open);
      document.addEventListener('click', this.handleClick);
      this.closeButtons.forEach((button) => button.addEventListener('click', this.close.bind(this)));
    }

    handleClick(evt) {
      const removeButton = evt.target.closest('.js-compare-col-remove');
      if (!removeButton) return;

      evt.preventDefault();
      CompareUtil.removeFromCompare(removeButton.dataset.productId, true);
      if (CompareUtil.getCompareCount() < 2) {
        this.close();
      } else {
        this.renderComparison();
      }
    }

    open() {
      if (CompareUtil.getCompareCount() < 2) return;
      this.modal.classList.remove('hidden');
      this.renderComparison();
      super.open();
    }

    handleTransitionEnd(evt) {
      super.handleTransitionEnd(evt);
      if (this.modal.open === false) this.modal.classList.add('hidden');
    }

    async renderComparison() {
      this.loadingSpinner.hidden = false;
      this.loadingSpinner.removeAttribute('aria-hidden');
      this.compareArea.classList.remove('revealable--in');
      this.compareArea.innerHTML = '';
      this.errorArea.hidden = true;
      this.differences.hidden = true;

      try {
        const selections = CompareUtil.getSelectedProducts();
        const products = await CompareModal.getCompareProducts(selections, this.copy.loadError);
        if (products.length !== selections.length) {
          const validIds = products.map((product) => String(product.selectionId));
          CompareUtil.setSelectedProducts(selections.filter((selection) => validIds.includes(String(selection.id))));
          CompareUtil.updateCompareCounters();
          CompareUtil.updateCompareControls();
          CompareUtil.updateShareUrl();
        }
        if (products.length < 2) throw new Error(this.copy.loadError);

        this.compareContainer.dataset.compareCount = products.length;
        this.renderDifferences(products);
        this.compareArea.innerHTML = this.renderTable(products);
      } catch (error) {
        this.errorArea.textContent = error.message || this.copy.loadError;
        this.errorArea.hidden = false;
      }

      this.loadingSpinner.hidden = true;
      this.loadingSpinner.setAttribute('aria-hidden', 'true');
      this.compareArea.classList.add('revealable--in');
    }

    renderDifferences(products) {
      const highlights = products.map(() => []);
      const add = (index, label) => {
        if (index >= 0 && !highlights[index].includes(label)) highlights[index].push(label);
      };
      const uniqueExtreme = (key, mode, label) => {
        const values = products.map((product, index) => ({
          index,
          value: CompareModal.numericSpec(product, key)
        })).filter((item) => Number.isFinite(item.value));
        if (values.length < 2) return;
        const target = mode === 'min'
          ? Math.min(...values.map((item) => item.value))
          : Math.max(...values.map((item) => item.value));
        const winners = values.filter((item) => item.value === target);
        if (winners.length === 1 && values.some((item) => item.value !== target)) add(winners[0].index, label);
      };

      uniqueExtreme('overallDepth', 'min', this.copy.highlights.shallower);
      uniqueExtreme('framingWidth', 'min', this.copy.highlights.lessFramingWidth);
      uniqueExtreme('framingDepth', 'min', this.copy.highlights.lessFramingDepth);
      uniqueExtreme('maximumBtu', 'max', this.copy.highlights.higherBtu);
      uniqueExtreme('maximumWattage', 'max', this.copy.highlights.higherWattage);

      const viewingAreas = products.map((product, index) => {
        const width = CompareModal.numericSpec(product, 'viewingWidth');
        const height = CompareModal.numericSpec(product, 'viewingHeight');
        return { index, value: Number.isFinite(width) && Number.isFinite(height) ? width * height : null };
      }).filter((item) => Number.isFinite(item.value));
      if (viewingAreas.length >= 2) {
        const maximum = Math.max(...viewingAreas.map((item) => item.value));
        const winners = viewingAreas.filter((item) => item.value === maximum);
        if (winners.length === 1 && viewingAreas.some((item) => item.value !== maximum)) {
          add(winners[0].index, this.copy.highlights.largerViewingArea);
        }
      }

      this.addBooleanDifference(products, highlights, 'hardwire', this.copy.highlights.hardwire);
      this.addBooleanDifference(products, highlights, 'plugIn', this.copy.highlights.plugIn);
      this.addDerivedDifference(products, highlights, CompareModal.multiSidedState, this.copy.highlights.multiSided);
      this.addDerivedDifference(products, highlights, CompareModal.appControlState, this.copy.highlights.appControl);
      this.addDerivedDifference(products, highlights, CompareModal.outdoorState, this.copy.highlights.outdoor);

      const configurationCounts = products.map((product, index) => ({
        index,
        value: CompareModal.asList(product.specs.installationConfigurations).length
      })).filter((item) => item.value > 0);
      if (configurationCounts.length >= 2) {
        const maximum = Math.max(...configurationCounts.map((item) => item.value));
        const winners = configurationCounts.filter((item) => item.value === maximum);
        if (maximum > 1 && winners.length === 1 && configurationCounts.some((item) => item.value !== maximum)) {
          add(winners[0].index, this.copy.highlights.installationFlexibility);
        }
      }

      const hasHighlights = highlights.some((items) => items.length);
      if (!hasHighlights) {
        this.differences.hidden = true;
        this.differencesGrid.innerHTML = '';
        return;
      }

      this.differences.hidden = false;
      this.differencesGrid.innerHTML = products.map((product, index) => {
        if (!highlights[index].length) return '';
        return `<article class="premium-compare-difference-card">
          <p>${CompareModal.escape(product.sku || product.brand || '')}</p>
          <h3>${CompareModal.escape(product.title)}</h3>
          <ul>${highlights[index].map((item) => `<li>${CompareModal.escape(item)}</li>`).join('')}</ul>
        </article>`;
      }).join('');
    }

    addBooleanDifference(products, highlights, key, label) {
      const states = products.map((product, index) => ({ index, state: product.specs[key] }));
      const hasTrue = states.some((item) => item.state && item.state.known && item.state.value === true);
      const hasFalse = states.some((item) => item.state && item.state.known && item.state.value === false);
      if (hasTrue && hasFalse) {
        states.filter((item) => item.state && item.state.known && item.state.value === true)
          .forEach((item) => highlights[item.index].push(label));
      }
    }

    addDerivedDifference(products, highlights, stateFunction, label) {
      const states = products.map((product, index) => ({ index, state: stateFunction(product) }));
      const hasTrue = states.some((item) => item.state.known && item.state.value);
      const hasFalse = states.some((item) => item.state.known && !item.state.value);
      if (hasTrue && hasFalse) {
        states.filter((item) => item.state.known && item.state.value)
          .forEach((item) => highlights[item.index].push(label));
      }
    }

    renderTable(products) {
      const groups = [
        { label: this.copy.groups.dimensions, fields: ['overallWidth', 'overallHeight', 'overallDepth'] },
        { label: this.copy.groups.framing, fields: ['framingWidth', 'framingHeight', 'framingDepth'] },
        { label: this.copy.groups.viewing, fields: ['viewingWidth', 'viewingHeight'] },
        { label: this.copy.groups.electrical, fields: ['maximumBtu', 'maximumWattage', 'heatingCapacity', 'voltage', 'amps', 'hardwire', 'plugIn'] },
        { label: this.copy.groups.installation, fields: ['installationType', 'installation', 'installationConfigurations', 'viewingStyle', 'bestFor', 'indoorOutdoor', 'heaterLocation'] },
        { label: this.copy.groups.experience, fields: ['flameColours', 'smartControl', 'remote', 'mediaType'] },
        { label: this.copy.groups.details, fields: ['brand', 'series', 'size', 'warranty', 'shippingWeight'] }
      ];

      const productHeaders = products.map((product) => `<th scope="col" class="premium-compare-product-head">
        <article>
          <div class="premium-compare-product-head__image">
            ${product.image && product.image.url ? `<img src="${CompareModal.escape(product.image.url)}" alt="${CompareModal.escape(product.image.alt || product.title)}" width="360" height="288">` : ''}
          </div>
          <p>${CompareModal.escape(product.brand || '')}</p>
          <h2>${CompareModal.escape(product.title)}</h2>
          ${product.sku ? `<span class="premium-compare-product-head__sku">${CompareModal.escape(product.sku)}</span>` : ''}
          <div class="premium-compare-product-head__price">${CompareModal.escape(product.price || '')}${product.compareAtPrice ? `<s>${CompareModal.escape(product.compareAtPrice)}</s>` : ''}</div>
          <span class="premium-compare-availability ${product.available ? 'is-available' : ''}">${CompareModal.escape(product.available ? this.copy.available : this.copy.unavailable)}</span>
          <div class="premium-compare-product-head__actions">
            <a href="${CompareModal.escape(product.url)}">${CompareModal.escape(this.copy.viewProduct)}</a>
            <button type="button" class="js-compare-col-remove" data-product-id="${CompareModal.escape(product.selectionId)}">${CompareModal.escape(this.copy.removeProduct)}</button>
          </div>
        </article>
      </th>`).join('');

      const body = groups.map((group) => {
        const visibleFields = group.fields.filter((field) => products.some((product) => this.hasFieldValue(product, field)));
        if (!visibleFields.length) return '';

        const rows = visibleFields.map((field) => {
          const values = products.map((product) => this.fieldDisplay(product, field));
          const normalized = values.map((value) => value.normalized);
          const differs = new Set(normalized).size > 1;
          return `<tr class="${differs ? 'has-difference' : ''}">
            <th scope="row">${CompareModal.escape(this.copy.fields[field])}</th>
            ${values.map((value) => `<td>${value.html}${differs ? `<span class="premium-compare-difference-note">${CompareModal.escape(this.copy.different)}</span>` : ''}</td>`).join('')}
          </tr>`;
        }).join('');

        return `<tr class="premium-compare-group-row"><th colspan="${products.length + 1}" scope="colgroup">${CompareModal.escape(group.label)}</th></tr>${rows}`;
      }).join('');

      return `<div class="premium-compare-table-wrap" tabindex="0" role="region" aria-label="${CompareModal.escape(this.copy.caption)}">
        <table class="premium-compare-table">
          <caption class="sr-only">${CompareModal.escape(this.copy.caption)}</caption>
          <thead><tr><th scope="col" class="premium-compare-spec-heading">${CompareModal.escape(this.copy.specification)}</th>${productHeaders}</tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>`;
    }

    hasFieldValue(product, field) {
      if (field === 'brand') return Boolean(product.brand);
      if (field === 'installation' && this.installationIsDuplicate(product)) return false;
      const value = product.specs[field];
      if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'display')) return Boolean(value.display);
      if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'known')) return value.known;
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && String(value).trim() !== '';
    }

    fieldDisplay(product, field) {
      if (!this.hasFieldValue(product, field)) {
        return { html: `<span class="premium-compare-missing">${CompareModal.escape(this.copy.missing)}</span>`, normalized: '__missing__' };
      }

      if (field === 'brand') {
        return { html: CompareModal.escape(product.brand), normalized: String(product.brand).toLowerCase() };
      }

      const value = product.specs[field];
      if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'display')) {
        const note = value.structured ? '' : `<small>${CompareModal.escape(this.copy.listedValue)}</small>`;
        return { html: `${CompareModal.escape(value.display)}${note}`, normalized: `${value.structured ? 'structured' : 'legacy'}:${value.display}` };
      }

      if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'known')) {
        const label = value.value ? this.copy.yes : this.copy.no;
        const icon = value.value ? '<span class="premium-compare-check" aria-hidden="true">✓</span>' : '';
        return { html: `${icon}${CompareModal.escape(label)}`, normalized: String(value.value) };
      }

      if (field === 'amps') {
        const formatted = CompareModal.formatAmps(value, product.specs.voltage);
        return { html: CompareModal.escape(formatted), normalized: formatted.toLowerCase() };
      }

      if (Array.isArray(value)) {
        const items = value.filter(Boolean);
        return {
          html: `<span class="premium-compare-pills">${items.map((item) => `<span>${CompareModal.escape(item)}</span>`).join('')}</span>`,
          normalized: items.join('|').toLowerCase()
        };
      }

      return { html: CompareModal.escape(value), normalized: String(value).trim().toLowerCase() };
    }

    installationIsDuplicate(product) {
      const installation = CompareModal.asList(product.specs.installation);
      if (installation.length !== 1 || !product.specs.installationType) return false;
      return CompareModal.normalizeToken(installation[0]) === CompareModal.normalizeToken(product.specs.installationType);
    }

    static numericSpec(product, key) {
      const value = product.specs[key];
      if (!value || !value.structured || !Number.isFinite(Number(value.number))) return null;
      return Number(value.number);
    }

    static multiSidedState(product) {
      const configurations = CompareModal.asList(product.specs.installationConfigurations);
      if (!configurations.length) return { known: false, value: false };
      return { known: true, value: configurations.some((item) => /multi|two.?sided|three.?sided|corner|bay/i.test(item)) };
    }

    static appControlState(product) {
      const value = String(product.specs.smartControl || '').trim();
      if (!value) return { known: false, value: false };
      return { known: true, value: !/^(no|false|none|not available)$/i.test(value) };
    }

    static outdoorState(product) {
      const value = String(product.specs.indoorOutdoor || '').trim();
      if (!value) return { known: false, value: false };
      return { known: true, value: /outdoor/i.test(value) };
    }

    static asList(value) {
      if (Array.isArray(value)) return value.filter(Boolean).map(String);
      return value ? [String(value)] : [];
    }

    static formatAmps(amps, voltage) {
      const ampsText = String(amps || '').trim();
      const voltageText = String(voltage || '').trim();
      if (!ampsText || /@|\bA\b/i.test(ampsText)) return ampsText;
      const ampParts = ampsText.split('/').map((part) => part.trim());
      const voltageParts = voltageText.split('/').map((part) => part.trim());
      if (ampParts.length === voltageParts.length && ampParts.length > 1) {
        return ampParts.map((part, index) => `${part}A @ ${voltageParts[index]}`).join(' / ');
      }
      return `${ampsText}A`;
    }

    static normalizeToken(value) {
      return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
    }

    static readCopy() {
      const script = document.getElementById('premium-compare-copy');
      return script ? JSON.parse(script.textContent) : {};
    }

    static escape(value) {
      return String(value === null || value === undefined ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    static async getCompareProducts(selections, errorMessage) {
      const products = await Promise.all(selections.map(async (selection) => {
        try {
          const response = await fetch(new URL(selection.url, window.location.origin), { credentials: 'same-origin' });
          if (!response.ok) return null;
          const template = document.createElement('template');
          template.innerHTML = await response.text();
          const dataScript = template.content.querySelector('[data-premium-compare-product]');
          if (!dataScript) return null;
          const product = JSON.parse(dataScript.textContent);
          product.selectionId = String(selection.id);
          return product;
        } catch (error) {
          return null;
        }
      }));

      return products.filter(Boolean);
    }
  }

  customElements.define('compare-modal', CompareModal);
});
